import { inject, Injectable } from '@angular/core'
import { Actions, createEffect, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { TrackingService } from 'src/app/api/tracking/tracking.service'
import { AppState } from 'src/app/core/store'
import * as fromTrackingActions from './tracking.actions'
import {
  catchError,
  concatMap,
  debounceTime,
  forkJoin,
  filter,
  map,
  of,
  switchMap,
  withLatestFrom,
  mergeMap,
} from 'rxjs'
import { mergeApiResponses, rejectError } from 'src/app/api/util'
import { sortByInceptionDate } from '../tracking.utils'
import { ProgramEventsService } from 'src/app/api/program-events/program-events.service'
import { ProgramEvents } from 'src/app/program-events/model/program-events.model'
import { SelectedUserPreferences } from 'src/app/core/model/reinsurer.model'
import { selectInceptionTimeInterval, selectTeamViewTrackingSelectors } from './tracking.selectors'
import {
  ColumnRequest,
  CommentRequest,
  TeamBulkRequest,
  TrackingData,
  TrackingPreferences,
  TrackingViewsData,
} from '../tracking.model'
import { UserPreferencesService } from 'src/app/api/user-preferences/user-preferences.service'
import { MatSnackBar } from '@angular/material/snack-bar'

@Injectable()
export class TrackingEffects {
  private service = inject(TrackingService)
  private programEventsService = inject(ProgramEventsService)
  private userPreferencesService = inject(UserPreferencesService)
  private _snackBar = inject(MatSnackBar)

  private actions$ = inject(Actions)
  private store = inject(Store<AppState>)

  fetchTracking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.fetchTracking),
      switchMap(() => {
        return this.service.getTracking()
      }),
      rejectError((error) =>
        this.store.dispatch(fromTrackingActions.fetchTrackingFailure({ error })),
      ),
      map((trackingData) => {
        const now = new Date()
        const month = now.getMonth() + 1
        let year = now.getFullYear()
        year = month <= 9 ? year : year + 1
        return fromTrackingActions.fetchTrackingSuccess({
          trackingData: sortByInceptionDate<TrackingData>(trackingData),
          selectedYear: String(year),
        })
      }),
    )
  })

  getTrackingPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.fetchTrackingSuccess),
      switchMap(() => this.userPreferencesService.getTrackingUserPreferences()),
      rejectError((error) =>
        this.store.dispatch(fromTrackingActions.getTrackingPreferencesFailure({ error })),
      ),
      map((userPreferences) => {
        // If no preferences -> just store empty & keep default state behavior
        if (!userPreferences || userPreferences.length === 0) {
          return fromTrackingActions.getTrackingPreferencesSuccess({
            userPreferences,
          })
        }

        // Otherwise pick the latest preference
        const latestPreference = userPreferences.slice().sort((a, b) => {
          const dateA = new Date(a.audit_update_dt ?? a.audit_insert_dt ?? 0).getTime()
          const dateB = new Date(b.audit_update_dt ?? b.audit_insert_dt ?? 0).getTime()
          return dateB - dateA
        })[0]

        return fromTrackingActions.getTrackingPreferencesSuccess({
          userPreferences: userPreferences,
          selectedPreferenceId: latestPreference.id,
        })
      }),
    )
  })

  fetchTrackingDetails$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.fetchTrackingDetails),
      concatMap((props) => {
        return this.service.getTrackingDetails(props.programId)
      }),
      rejectError((error) =>
        this.store.dispatch(fromTrackingActions.fetchTrackingDetailsFailure({ error })),
      ),
      map((trackingDetailsData) => {
        return fromTrackingActions.fetchTrackingDetailsSuccess({
          trackingDetailsData,
        })
      }),
    )
  })

  updateCompleteProgram$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.updateCompleteProgram),
      concatMap((props) => {
        const programEvents: ProgramEvents = {
          eventType: 'Complete',
          programId: props.programId.toString(),
        }
        return this.programEventsService.saveOrUpdateProgramEvents(programEvents)
      }),
      rejectError((error) =>
        this.store.dispatch(
          fromTrackingActions.updateCompleteProgramFailure({
            error,
          }),
        ),
      ),
      map((data) => {
        return fromTrackingActions.updateCompleteProgramSuccess({
          programEvents: data,
        })
      }),
    )
  })

  saveTrackingPreferences$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.saveTrackingPreferences),
      withLatestFrom(
        this.store.pipe(select(selectTeamViewTrackingSelectors)),
        this.store.pipe(select(selectInceptionTimeInterval)),
      ),
      concatMap(([props, selectors, timeInterval]) => {
        const preferences: TrackingPreferences = {
          label: props.label,
          clientName: props.filters?.clientName ?? selectors.clientName.selectedValues,
          year: props.filters?.year ?? selectors.year.selectedValues,
          businessUnit: props.filters?.businessUnit ?? selectors.businessUnit.selectedValues,
          reinsurerNames: props.filters?.reinsurerNames ?? selectors.reinsurerNames.selectedValues,
          complete: props.filters?.complete ?? selectors.complete.selectedValues,
          maxDateValue: timeInterval.maxValue ?? '',
          minDateValue: timeInterval.minValue ?? '',
        }
        const userPreference: SelectedUserPreferences = {
          preferenceValue: JSON.stringify(preferences),
          preferenceType: 'Tracking',
        }
        return this.userPreferencesService.saveUserPreferences(userPreference)
      }),
      rejectError((error) =>
        this.store.dispatch(
          fromTrackingActions.saveTrackingPreferencesFailure({
            error,
          }),
        ),
      ),
      map((data) => {
        return fromTrackingActions.saveTrackingPreferencesSuccess({
          userPreferences: data,
        })
      }),
    )
  })

  updateUserPreference$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.updateUserPreference),
      withLatestFrom(
        this.store.pipe(select(selectTeamViewTrackingSelectors)),
        this.store.pipe(select(selectInceptionTimeInterval)),
      ),
      concatMap(([props, selectors, timeInterval]) => {
        const parsedObject = JSON.parse(props.updatedPreferences.preferenceValue)
        const labelValue = parsedObject.label
        const preferences: TrackingPreferences = {
          label: labelValue,
          clientName: selectors.clientName.selectedValues,
          year: selectors.year.selectedValues,
          businessUnit: selectors.businessUnit.selectedValues,
          reinsurerNames: selectors.reinsurerNames.selectedValues,
          complete: selectors.complete.selectedValues,
          maxDateValue: timeInterval.maxValue ?? '',
          minDateValue: timeInterval.minValue ?? '',
        }
        const userPreference: SelectedUserPreferences = {
          preferenceValue: JSON.stringify(preferences),
          preferenceType: 'Tracking',
          id: props.updatedPreferences.id,
          userId: props.updatedPreferences.userId,
        }
        return this.userPreferencesService.updateUserPreference(userPreference)
      }),
      rejectError((error) =>
        this.store.dispatch(
          fromTrackingActions.updateUserPreferenceFailure({
            error,
          }),
        ),
      ),
      map((data) => {
        this._snackBar.open('The view has been successfully updated.', 'X', {
          duration: 2000,
        })
        return fromTrackingActions.updateUserPreferenceSuccess({
          updatedPreferences: data,
        })
      }),
    )
  })

  deleteUserPreference$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromTrackingActions.deleteUserPreference),
      mergeMap(({ deletedPreferences }) =>
        this.service.deleteUserPreference(deletedPreferences).pipe(
          map(() => {
            this._snackBar.open('The view has been successfully deleted.', 'X', {
              duration: 2000,
            })
            return fromTrackingActions.deleteUserPreferenceSuccess({ deletedPreferences })
          }),
          catchError((error) => of(fromTrackingActions.deleteUserPreferenceFailure({ error }))),
        ),
      ),
    ),
  )

  fetchMyViewTracking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.fetchMyViewTracking),
      withLatestFrom(this.store.pipe(select(selectInceptionTimeInterval))),
      switchMap(([action, inceptionInterval]) =>
        this.service
          .getMyViewTracking(inceptionInterval?.minValue, inceptionInterval?.maxValue)
          .pipe(
            rejectError((error) =>
              this.store.dispatch(fromTrackingActions.fetchMyViewTrackingFailure({ error })),
            ),
            map((trackingMyViewsData) => {
              const applyDefaultYear = action.applyDefaultYear ?? true
              let selectedYear: string | undefined
              if (applyDefaultYear) {
                const now = new Date()
                const month = now.getMonth() + 1
                let year = now.getFullYear()
                year = month <= 9 ? year : year + 1
                selectedYear = String(year)
              }
              return fromTrackingActions.fetchMyViewTrackingSuccess({
                trackingMyViewsData: sortByInceptionDate<TrackingViewsData>(trackingMyViewsData),
                selectedYear,
              })
            }),
          ),
      ),
    )
  })

  fetchTeamViewTracking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.fetchTeamViewTracking),
      withLatestFrom(this.store.pipe(select(selectInceptionTimeInterval))),
      switchMap(([action, inceptionInterval]) =>
        this.service
          .getTeamViewTracking(inceptionInterval?.minValue, inceptionInterval?.maxValue)
          .pipe(
            rejectError((error) =>
              this.store.dispatch(fromTrackingActions.fetchTeamViewTrackingFailure({ error })),
            ),
            map((trackingTeamViewsData) => {
              const applyDefaultYear = action.applyDefaultYear ?? true
              let selectedYear: string | undefined
              if (applyDefaultYear) {
                const now = new Date()
                const month = now.getMonth() + 1
                let year = now.getFullYear()
                year = month <= 9 ? year : year + 1
                selectedYear = String(year)
              }
              return fromTrackingActions.fetchTeamViewTrackingSuccess({
                trackingTeamViewsData:
                  sortByInceptionDate<TrackingViewsData>(trackingTeamViewsData),
                selectedYear,
              })
            }),
          ),
      ),
    )
  })

  inceptionDateChangedRefetch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.inceptionDateChanged),
      debounceTime(400),
      switchMap(() => [
        fromTrackingActions.fetchTeamViewTracking({ applyDefaultYear: false }),
        fromTrackingActions.fetchMyViewTracking({ applyDefaultYear: false }),
        fromTrackingActions.fetchClientViewTracking(),
      ]),
    )
  })

  // When a saved preference is applied (manually or auto-selected on load), the
  // inception date range can change. Refetch the view data with the new range so
  // all records within that range are loaded, not just the default-range records.
  preferenceAppliedRefetch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.setSelectedUserPreferences),
      switchMap(() => [
        fromTrackingActions.fetchTeamViewTracking({ applyDefaultYear: false }),
        fromTrackingActions.fetchMyViewTracking({ applyDefaultYear: false }),
        fromTrackingActions.fetchClientViewTracking(),
      ]),
    )
  })

  autoAppliedPreferenceRefetch$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.getTrackingPreferencesSuccess),
      filter((action) => !!action.selectedPreferenceId),
      switchMap(() => [
        fromTrackingActions.fetchTeamViewTracking({ applyDefaultYear: false }),
        fromTrackingActions.fetchMyViewTracking({ applyDefaultYear: false }),
        fromTrackingActions.fetchClientViewTracking(),
      ]),
    )
  })

  fetchClientViewTracking$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.fetchClientViewTracking),
      withLatestFrom(this.store.pipe(select(selectInceptionTimeInterval))),
      switchMap(([, inceptionInterval]) =>
        this.service.getClientViewTracking(
          inceptionInterval?.minValue,
          inceptionInterval?.maxValue,
        ),
      ),
      rejectError((error) =>
        this.store.dispatch(fromTrackingActions.fetchClientViewTrackingFailure({ error })),
      ),
      map((trackingViewsData) =>
        fromTrackingActions.fetchClientViewTrackingSuccess({
          trackingClientViewsData: trackingViewsData,
        }),
      ),
    )
  })

  refreshViews$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(fromTrackingActions.refreshViews),
      withLatestFrom(this.store.pipe(select(selectInceptionTimeInterval))),
      switchMap(([, inceptionInterval]) =>
        forkJoin([
          this.service.getMyViewTracking(
            inceptionInterval?.minValue || undefined,
            inceptionInterval?.maxValue || undefined,
          ),
          this.service.getTeamViewTracking(
            inceptionInterval?.minValue || undefined,
            inceptionInterval?.maxValue || undefined,
          ),
          this.service.getClientViewTracking(
            inceptionInterval?.minValue || undefined,
            inceptionInterval?.maxValue || undefined,
          ),
        ]).pipe(mergeApiResponses()),
      ),
      rejectError((error) =>
        this.store.dispatch(fromTrackingActions.fetchTrackingDetailsFailure({ error })),
      ),
      switchMap(([trackingMyViewsData, trackingTeamViewsData, trackingClientViewsData]) => [
        fromTrackingActions.fetchMyViewTrackingSuccess({
          trackingMyViewsData: sortByInceptionDate<TrackingViewsData>(trackingMyViewsData),
        }),
        fromTrackingActions.fetchTeamViewTrackingSuccess({
          trackingTeamViewsData: sortByInceptionDate<TrackingViewsData>(trackingTeamViewsData),
        }),
        fromTrackingActions.fetchClientViewTrackingSuccess({
          trackingClientViewsData: sortByInceptionDate<TrackingViewsData>(trackingClientViewsData),
        }),
      ]),
    )
  })

  updateColumnValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromTrackingActions.updateColumnValue),
      concatMap((props) => {
        const columnValue: ColumnRequest = {
          category: props.category,
          value: props.newValue,
          date: props.date,
        }

        return this.service.updateColumnValue(props.opportunityId, columnValue).pipe(
          map((data) =>
            fromTrackingActions.updateColumnValueSuccess({
              opportunityId: data.status.opportunityId,
              category: columnValue.category,
              newValue: data.status.value,
              dateTime: data.status.date!,
              viewType: props.viewType,
            }),
          ),
          catchError((error) => of(fromTrackingActions.updateColumnValueFailure({ error }))),
        )
      }),
    ),
  )

  updateUserValue$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromTrackingActions.updateUserValue),
      concatMap((props) => {
        const teamBulkRequest: TeamBulkRequest = {
          role: props.role,
          email_ids: props.email_ids,
        }

        return this.service.updateUserValue(props.opportunityId, teamBulkRequest).pipe(
          switchMap(() => {
            return [
              fromTrackingActions.updateUserValueSuccess({
                opportunityId: String(props.opportunityId),
                role: teamBulkRequest.role,
                email_ids: teamBulkRequest.email_ids,
                viewType: props.viewType,
              }),
              fromTrackingActions.refreshViews(),
            ]
          }),
          catchError((error) => of(fromTrackingActions.updateUserValueFailure({ error }))),
        )
      }),
    ),
  )

  updateComment$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromTrackingActions.updateComment),
      concatMap((props) => {
        const columnValue: CommentRequest = {
          category: props.category,
          comment: props.comment,
        }

        return this.service.updateComment(props.opportunityId, columnValue).pipe(
          map((data) => {
            return fromTrackingActions.updateCommentSuccess({
              opportunityId: data.opportunityId,
              category: columnValue.category,
              comment: data.text,
              viewType: props.viewType,
            })
          }),
          catchError((error) => of(fromTrackingActions.updateCommentFailure({ error }))),
        )
      }),
    ),
  )
}
